import { tokens } from '@/styles/tokens';
import { styled } from '@mui/material/styles';
import type { useSortable } from '@dnd-kit/sortable';

export type DropEdge = 'top' | 'bottom';

/** The subset of `useSortable`'s return value the drop-line logic needs. */
type SortableDropState = Pick<
  ReturnType<typeof useSortable>,
  'isOver' | 'isDragging' | 'activeIndex' | 'index' | 'active'
>;

/**
 * Where the single drop-line should sit for a sortable item, or `null` when the
 * item is not the current drop target.
 *
 * The edge mirrors `arrayMove` semantics so the line always marks where the
 * dragged item will actually land:
 * - dragging downward (active sits above the target) → line below the target
 * - dragging upward (active sits below the target) → line above the target
 * - cross-list drag (active not in this list) → line above (insert before)
 *
 * The same rule runs sideways in a row of chips (dragging right lands after the target,
 * left before it). A row that WRAPS also has space between items the rule cannot answer
 * for; the caller resolves that by geometry and passes the `left`/`right` edge itself.
 *
 * It deliberately does not depend on pointer position: with reflow disabled the
 * sortable rects stay put, so `isOver` + index comparison is stable.
 */
export function getSortableDropEdge(state: SortableDropState): DropEdge | null {
  const { isOver, isDragging, activeIndex, index, active } = state;
  if (!active || isDragging || !isOver) return null;
  if (activeIndex < 0) return 'top';
  return activeIndex < index ? 'bottom' : 'top';
}

interface DropLineProps {
  /**
   * `top`/`bottom` draw a horizontal rule between stacked rows, `left`/`right` a
   * vertical one between chips in a row.
   */
  edge: DropEdge | 'left' | 'right';
  /**
   * The gap between sibling items — vertical for stacked rows, horizontal for chips;
   * the line is centred within it.
   */
  gap?: number;
  /**
   * Inset in px along the rule, so it stops short of the item's edges instead of
   * running its full length. Defaults to 0 — the list surfaces that shipped first draw
   * edge to edge, and only the pivot's Figma insets it (36760-153136). The leading dot
   * keeps its offset relative to the rule, so it travels with the inset.
   */
  inset?: number;
}

/**
 * The vertical rule: it stands in the gap between two chips, as tall as the item, with
 * the dot at its top (PAT-16) — the same 2px rule and 8px dot as the horizontal line.
 */
const VerticalRule = styled('span', {
  shouldForwardProp: (prop) => prop !== 'edge' && prop !== 'offset' && prop !== 'inset',
})<{ edge: 'left' | 'right'; offset: number; inset: number }>(({ theme, edge, offset, inset }) => ({
  position: 'absolute',
  top: inset,
  bottom: inset,
  [edge]: offset,
  width: 2,
  backgroundColor: theme.palette.primary.main,
  pointerEvents: 'none',
  zIndex: 5,
  '&::before': {
    content: '""',
    position: 'absolute',
    top: -2,
    left: '50%',
    transform: 'translateX(-50%)',
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: theme.palette.primary.main,
  },
}));

/**
 * The app-wide insertion indicator (used by requests DnD, the Lead-sheets pivot
 * remap, the data-analysis chip bar, etc.) — a single line + leading dot, absolutely
 * positioned so it occupies no layout space and never shifts the list. Horizontal
 * between stacked rows, dot at the left; vertical between chips in a row, dot at the
 * top. The parent must be `position: relative`.
 *
 * Style per Figma "drop indicator style" (34695-327559): a 2px teal rule with a
 * solid 8px teal dot at the leading edge. One source of truth so every drop
 * indicator looks identical.
 */
export function DropLine({ edge, gap = 12, inset = 0 }: DropLineProps) {
  const offset = -(gap / 2 + 1);

  if (edge === 'left' || edge === 'right') {
    return (
      <VerticalRule
        aria-hidden
        data-testid='dnd-drop-line'
        data-edge={edge}
        edge={edge}
        offset={offset}
        inset={inset}
      />
    );
  }

  return (
    <span
      aria-hidden
      data-testid='dnd-drop-line'
      data-edge={edge}
      style={{
        position: 'absolute',
        left: inset,
        right: inset,
        top: edge === 'top' ? offset : undefined,
        bottom: edge === 'bottom' ? offset : undefined,
        height: 2,
        backgroundColor: tokens.primary.main,
        pointerEvents: 'none',
        zIndex: 5,
      }}
    >
      <span
        style={{
          position: 'absolute',
          left: -2,
          top: '50%',
          transform: 'translateY(-50%)',
          width: 8,
          height: 8,
          borderRadius: '50%',
          backgroundColor: tokens.primary.main,
        }}
      />
    </span>
  );
}
